/**
 * Amazon Image URL Transformer Utility
 *
 * This utility transforms Amazon CDN image URLs to use proper sizing parameters,
 * optimizing image loading for different contexts (grid, detail pages, thumbnails)
 *
 * Example URL structure:
 * https://images-na.ssl-images-amazon.com/images/I/61QLCOJdi7L._AC_UL232_SR232,232_.jpg
 *
 * Common Parameters:
 * _AC_ - Amazon Catalog (standard quality)
 * _SL###_ - Scale Longest side to ### pixels (maintains aspect ratio)
 * _SS###_ - Scale Shortest side to ### pixels (maintains aspect ratio)
 * _UL###_ - Upper Limit (max dimension)
 * _SR###,###_ - Scale to exact dimensions (width,height)
 */

/**
 * Image size presets for different display contexts
 */
export const IMAGE_SIZE = {
  // Small images for grids and thumbnails
  THUMBNAIL: '_AC_UL160_SR160,160_',
  SMALL_GRID: '_AC_UL200_SR200,200_',
  MEDIUM_GRID: '_AC_UL232_SR232,232_',
  LARGE_GRID: '_AC_UL300_SR300,300_',

  // Larger images for product pages
  SMALL_DETAIL: '_AC_SL400_',
  MEDIUM_DETAIL: '_AC_SL600_',
  LARGE_DETAIL: '_AC_SL500_',
  FULL_SIZE: '_AC_SL1500_',
};

/**
 * Regular expression to detect Amazon image URLs
 * Matches both traditional Amazon CDN domains:
 * - images-na.ssl-images-amazon.com
 * - m.media-amazon.com
 */
const AMAZON_IMAGE_REGEX = /(images-\w+\.ssl-images-amazon\.com|m\.media-amazon\.com)\/images\/\w+\/[^_]+/;

/**
 * Returns a transformed Amazon image URL with appropriate size parameters
 *
 * @param {string} url - Original Amazon image URL
 * @param {string} size - Size preset from IMAGE_SIZE enum
 * @param {boolean} preserveExisting - Whether to keep existing parameters if present
 * @return {string} Transformed URL with size parameters
 */
export const transformAmazonImageUrl = (url, size = IMAGE_SIZE.MEDIUM_GRID, preserveExisting = false) => {
  // If URL is missing or not an Amazon image URL, return as is
  if (!url || !AMAZON_IMAGE_REGEX.test(url)) {
    return url;
  }

  // Handle case where URL already has parameters
  if (url.includes('._')) {
    // If we want to preserve existing parameters, return as is
    if (preserveExisting) {
      return url;
    }

    // Otherwise, extract the base part before parameters
    const baseUrl = url.split('._')[0];
    return `${baseUrl}.${size}.jpg`;
  }

  // Simple case - URL doesn't have parameters yet
  // First determine if we need to add an extension
  if (url.endsWith('.jpg') || url.endsWith('.png') || url.endsWith('.jpeg')) {
    // Insert parameters before the extension
    const extension = url.substring(url.lastIndexOf('.'));
    const baseUrl = url.substring(0, url.lastIndexOf('.'));
    return `${baseUrl}.${size}${extension}`;
  }

  // URL doesn't have an extension, just append
  return `${url}.${size}.jpg`;
};

/**
 * Gets appropriate image size based on screen width for responsive images
 *
 * @param {number} screenWidth - Current screen width
 * @return {string} Appropriate image size parameter
 */
export const getResponsiveImageSize = (screenWidth) => {
  if (screenWidth < 576) {
    return IMAGE_SIZE.SMALL_GRID;      // Mobile
  }
  if (screenWidth < 768) {
    return IMAGE_SIZE.MEDIUM_GRID;     // Tablet
  }
  if (screenWidth < 1200) {
    return IMAGE_SIZE.LARGE_GRID;      // Desktop
  }
  return IMAGE_SIZE.MEDIUM_DETAIL;     // Large desktop
};
